import { useEffect, useRef, useState } from 'react';
import { getApiBaseUrl } from '../services/api';

/**
 * Listens to a server-sent events stream (e.g. /audits/12/events) with the
 * signed-in user's token. Uses fetch rather than EventSource so the token
 * goes in the Authorization header, not the URL (URLs end up in logs).
 * Reconnects with a growing delay (2 s … 60 s) when the stream drops.
 *
 * onEvent({ type, audit_id, data, at }) for every event except "ready".
 * onStatus('connecting' | 'live' | 'offline').
 * Returns a function that stops listening.
 */
export function listenToEvents(path, onEvent, onStatus = () => {}) {
  let stopped = false;
  let controller = null;
  let retryTimer = null;
  let attempt = 0;

  const scheduleRetry = () => {
    if (stopped) return;
    onStatus('offline');
    const delay = Math.min(60000, 2000 * 2 ** Math.min(attempt, 5));
    attempt += 1;
    retryTimer = setTimeout(connect, delay);
  };

  async function connect() {
    if (stopped) return;
    onStatus('connecting');
    controller = new AbortController();
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiBaseUrl()}${path}`, {
        headers: {
          Accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        signal: controller.signal,
        cache: 'no-store',
      });
      if (!response.ok || !response.body) {
        // 401/403 will not fix themselves quickly; retry slowly.
        if (response.status === 401 || response.status === 403) attempt = 5;
        scheduleRetry();
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        // eslint-disable-next-line no-await-in-loop
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let boundary = buffer.indexOf('\n\n');
        while (boundary >= 0) {
          const chunk = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          const dataLine = chunk
            .split('\n')
            .filter((line) => line.startsWith('data:'))
            .map((line) => line.slice(5).trimStart())
            .join('\n');
          if (dataLine) {
            try {
              const event = JSON.parse(dataLine);
              if (event.type === 'ready') {
                attempt = 0;
                onStatus('live');
              } else {
                onEvent(event);
              }
            } catch {
              // Ignore malformed chunks.
            }
          }
          boundary = buffer.indexOf('\n\n');
        }
      }
      scheduleRetry();
    } catch (error) {
      if (!stopped) scheduleRetry();
    }
  }

  connect();

  return () => {
    stopped = true;
    clearTimeout(retryTimer);
    if (controller) controller.abort();
  };
}

/**
 * React hook: calls onChange(events) at most once per `debounceMs` after
 * events arrive on `path` (null path = not listening). Returns the stream
 * status so pages can show "Live" and slow their fallback polling.
 */
export function useLiveEvents(path, onChange, { debounceMs = 1500, types = null } = {}) {
  const [status, setStatus] = useState('connecting');
  const onChangeRef = useRef(onChange);
  const typesKey = types ? types.join(',') : '';

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!path) return undefined;
    const allowed = typesKey ? new Set(typesKey.split(',')) : null;
    let pending = [];
    let timer = null;

    const stop = listenToEvents(
      path,
      (event) => {
        if (allowed && !allowed.has(event.type)) return;
        pending.push(event);
        if (timer) return;
        timer = setTimeout(() => {
          const batch = pending;
          pending = [];
          timer = null;
          onChangeRef.current(batch);
        }, debounceMs);
      },
      setStatus,
    );

    return () => {
      stop();
      clearTimeout(timer);
    };
  }, [path, debounceMs, typesKey]);

  return status;
}
